import type { TypeaheadEndResponse, TypeaheadExtractionData, TypeaheadLocation, TypeaheadNaturalLanguageSuggestionV2 } from '@luxuryescapes/contract-search'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import {
  mapFlexibleNightsToDurationRange,
  convertDateRangesToMonthString,
} from 'lib/search/flexibleDateUtils'
import type {
  TypeaheadNaturalLanguageResultV2,
  TypeaheadNaturalLanguageSuggestionsV2Display,
} from '@luxuryescapes/contract-search/types/sockets'

/**
 * Maps End event response to App.SearchItemV2
 */
export function mapTypeaheadEndEventToSearchItemV2(result: TypeaheadEndResponse): App.SearchItemV3 {
  const { searchItem, input } = result
  const { extractedData, typeaheadId } = searchItem

  return {
    searchType: 'searchItemV3',
    value: input,
    format: {
      mainText: input,
    },
    display: {
      mainText: input,
      searchType: 'naturalLanguage',
    },
    searchItem: {
      typeaheadId,
      extractedData: mapExtractionData(extractedData, input),
      extracting: false,
    },
  }
}

/**
 * Maps TypeaheadNaturalLanguageSuggestionV2 to App.SearchItemV2
 */
export function mapTypeaheadSuggestionToSearchItemV2(result: TypeaheadNaturalLanguageSuggestionV2): App.SearchItemV3 {
  const { suggestion, searchItem } = result
  const { extractedData, typeaheadId } = searchItem

  return {
    searchType: 'searchItemV3',
    value: suggestion,
    format: {
      mainText: suggestion,
    },
    display: {
      mainText: suggestion,
      searchType: 'naturalLanguage',
    },
    searchItem: {
      typeaheadId,
      extractedData: mapExtractionData(extractedData, suggestion),
      extracting: false,
    },
  }
}

/**
 * Maps contract searchType to App.SearchItemV2 display searchType
 * Handles snake_case to camelCase conversion for 'lat_long' -> 'latLong'
 */
export function mapDisplaySearchType(
  searchType: TypeaheadNaturalLanguageSuggestionsV2Display['searchType'],
): App.SearchItemV3['display']['searchType'] {
  if (searchType === 'lat_long') return 'latLong'
  return searchType
}

/**
 * Maps contract TypeaheadLocation to App.TypeaheadExtractedLocation
 * Handles snake_case to camelCase conversion for searchType ('lat_long' -> 'latLong')
 */
export function mapTypeaheadLocation(location: TypeaheadLocation | undefined): App.TypeaheadExtractedLocation | undefined {
  if (!location) return undefined

  // Lat/lng location
  if ('lat' in location) {
    return {
      lat: String(location.lat),
      lng: String(location.lng),
      searchType: 'latLong',
    }
  }

  // Place-based location
  return {
    placeIds: location.placeIds,
    searchType: mapDisplaySearchType(location.searchType),
  }
}

export function mapTypeaheadResultToSearchItemV2(result: TypeaheadNaturalLanguageResultV2): App.SearchItemV3 {
  const { display, searchItem } = result
  const { extractedData, typeaheadId } = searchItem

  return {
    searchType: 'searchItemV3',
    value: display.mainText,
    format: {
      mainText: display.mainText,
      secondaryText: display.secondaryText,
    },
    display: {
      mainText: display.mainText,
      secondaryText: display.secondaryText,
      imageId: display.imageId,
      searchType: mapDisplaySearchType(display.searchType),
      offerId: display.offerId,
    },
    searchItem: {
      typeaheadId,
      extractedData: mapExtractionData(extractedData, display.mainText),
      extracting: false,
    },
  }
}

export function getDisplayPlaceId(data: TypeaheadExtractionData): string | undefined {
  if (data?.property?.propertyId) return undefined
  const loc = data?.location
  if (!loc || !('placeIds' in loc)) return undefined
  const first = loc.placeIds[0]
  if (!first || first === ANYWHERE_PLACE_ID) return undefined
  return first
}

/**
 * Maps contract searchType to ExtractedSearchParamsV2 searchType
 * Train stations, metro stations, and airports are treated as landmarks (proximity-based search)
 */
function mapSearchType(type: string): 'destination' | 'landmark' | 'latLong' {
  if (type === 'landmark' || type === 'airport' || type === 'train_station' || type === 'metro_station') return 'landmark'
  if (type === 'lat_long') return 'latLong'
  return 'destination'
}

/**
 * Maps TypeaheadExtractionData to ExtractedSearchParamsV2
 */
function mapExtractionData(data: TypeaheadExtractionData, query: string): App.ExtractedSearchParamsV2 {
  const baseParams = mapBaseParams(data, query)
  // Property search
  if (data?.property?.propertyId) {
    return { ...baseParams, property: data.property, searchType: 'property' }
  }

  // Location search with lat/lng
  if (data.location?.searchType === 'lat_long' || data.location?.searchType === 'address') {
    return {
      ...baseParams,
      lat: String(data.location.lat),
      lng: String(data.location.lng),
      searchType: 'latLong',
    }
  }

  // Location search with placeIds
  if (
    data.location &&
    'placeIds' in data.location
  ) {
    return {
      ...baseParams,
      placeIds: data.location.placeIds,
      searchType: mapSearchType(data.location.searchType),
    }
  }

  // Default: anywhere search
  return {
    ...baseParams,
    placeIds: [ANYWHERE_PLACE_ID],
    searchType: 'destination',
  }
}

/**
 * Maps TypeaheadStreamingData to ExtractedSearchParamsV2
 * Maps directly without intermediate type conversion
 */
export function mapTypeaheadStreamingDataToExtractedSearchParams(
  data: App.TypeaheadStreamingData,
  query: string,
): App.ExtractedSearchParamsV2 {
  const baseParams = mapStreamingBaseParams(data, query)

  // Property search
  if (data.property?.data?.propertyId) {
    return { ...baseParams, property: data.property.data, searchType: 'property' }
  }

  // Location search
  const location = data.location?.data
  if (location && 'lat' in location) {
    return {
      ...baseParams,
      lat: location.lat,
      lng: location.lng,
      searchType: 'latLong',
    }
  }

  if (location && 'placeIds' in location) {
    return {
      ...baseParams,
      placeIds: location.placeIds,
      searchType: mapSearchType(location.searchType),
    }
  }

  // Default: anywhere search
  return {
    ...baseParams,
    placeIds: [ANYWHERE_PLACE_ID],
    searchType: 'destination',
  }
}

/**
 * Maps streaming data base params to ExtractedSearchParamsBase
 */
function mapStreamingBaseParams(
  data: App.TypeaheadStreamingData,
  query: string,
): App.ExtractedSearchParamsBase {
  return mapBaseParamsFromValues({
    query,
    dates: data.travelDates?.data,
    occupancy: data.occupancy?.data,
    amenities: data.amenities?.data,
    inclusions: data.inclusions?.data,
    inclusionGap: data.inclusionGap?.data,
    shouldSemanticRerank: data.shouldSemanticRerank?.data,
    tags: data.tags?.data,
    chains: data.chains?.data,
    subChains: data.subChains?.data,
    sortBy: data.sortBy?.data,
    price: data.price?.data,
  })
}

/**
 * Maps common base params from TypeaheadExtractionData
 */
function mapBaseParams(data: TypeaheadExtractionData, query: string): App.ExtractedSearchParamsBase {
  return mapBaseParamsFromValues({
    query,
    dates: data.dates,
    occupancy: data.occupancy,
    amenities: data.amenities,
    inclusions: data.inclusions,
    inclusionGap: data.inclusionGap,
    shouldSemanticRerank: data.shouldSemanticRerank,
    tags: data.tags,
    chains: data.chains,
    subChains: data.subChains,
    sortBy: data.sortBy,
    price: data.price,
  })
}

/**
 * Shared helper to map base params from normalized values
 * Used by both mapBaseParams (contract data) and mapStreamingBaseParams (streaming data)
 */
interface BaseParamsInput {
  query: string
  dates?: App.TypeaheadExtractedTravelDates
  occupancy?: Array<{ adults: number; childrenAges: Array<number> }>
  amenities?: Array<string>
  inclusions?: Array<string>
  inclusionGap?: string
  shouldSemanticRerank?: boolean
  tags?: Array<string>
  chains?: Array<string>
  subChains?: Array<string>
  sortBy?: 'value'
  price?: { priceGte?: number; priceLte?: number }
}

function mapBaseParamsFromValues(input: BaseParamsInput): App.ExtractedSearchParamsBase {
  const base: App.ExtractedSearchParamsBase = { query: input.query }

  // Map dates
  if (input.dates) {
    if (input.dates.type === 'specific') {
      base.checkIn = input.dates.checkIn
      base.checkOut = input.dates.checkOut
    } else if (input.dates.type === 'flexible') {
      base.flexibleNights = mapFlexibleNightsToDurationRange(input.dates.numberOfNights)
      base.flexibleMonths = convertDateRangesToMonthString(input.dates.dateRanges)
    }
  }

  // Map occupancy
  if (input.occupancy && input.occupancy.length > 0) {
    base.occupancy = input.occupancy.map((room) => ({
      adults: room.adults,
      children: room.childrenAges.length,
      childrenAge: room.childrenAges,
    }))
  }

  // Map filters
  if (input.amenities) base.amenities = input.amenities
  if (input.inclusions) base.inclusions = input.inclusions
  if (input.inclusionGap) base.inclusionGap = input.inclusionGap
  if (input.shouldSemanticRerank === true) base.semanticRerank = true
  if (input.tags) base.tags = input.tags
  if (input.chains) base.chains = input.chains
  if (input.subChains) base.subChains = input.subChains
  if (input.sortBy) base.sortBy = input.sortBy

  // Map price
  if (input.price) {
    if (input.price.priceGte !== undefined) base.priceGte = String(input.price.priceGte)
    if (input.price.priceLte !== undefined) base.priceLte = String(input.price.priceLte)
  }

  return base
}
